import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';
import LoadingOverlay from '../LoadingOverlay';

function Stock() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        const startDate = getFormattedStartDate();
        const currentDate = getFormattedCurrentDate();
        setFromDate(startDate);
        setToDate(currentDate);
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        if (fromDate && toDate) {
            fetchTableData(fromDate, toDate);
        }
    }, [fromDate, toDate]);

    const totals = useMemo(() => {
        return dataItem.reduce((acc, item) => ({
            totalopbal: acc.totalopbal + item.OpeningQty,
            totalp: acc.totalp + item.PurchaseQty,
            totalprtn: acc.totalprtn + item.PurchaseReturnQty,
            totals: acc.totals + item.SaleQty,
            totalsrtn: acc.totalsrtn + item.SaleReturnQty,
            totalstin: acc.totalstin + item.StockInQty,
            totalstout: acc.totalstout + item.StockOutQty,
            totalrpl: acc.totalrpl + item.ReplaceQty,
            totalcls: acc.totalcls + item.ClosingQty,
            totalstkval: acc.totalstkval + item.StockValue,
            totalsalval: acc.totalsalval + item.SaleValue
        }), {
            totalopbal: 0, totalp: 0, totalprtn: 0, totals: 0, totalsrtn: 0,
            totalstin: 0, totalstout: 0, totalrpl: 0, totalcls: 0, totalstkval: 0, totalsalval: 0
        });
    }, [dataItem]);

    const fetchCustomerData = () => {};

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];

    const getFormattedStartDate = () => {
        const d = new Date();
        d.setDate(1);
        return d.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);
    const loadingRef = useRef(false);

    const fetchTableData = useCallback(async (fDate, tDate) => {
        const effectiveFromDate = fDate || fromDate;
        const effectiveToDate = tDate || toDate;
        if (hasReadReportPermission && !loadingRef.current && effectiveFromDate && effectiveToDate) {
            loadingRef.current = true;
            setLoading(true);
            try {
                const response = await fetchData('ReportApi', `/Stock/${companyId}/${branchId}/${effectiveFromDate}/${effectiveToDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching Stock Details:', error);
            } finally {
                loadingRef.current = false;
                setLoading(false);
            }
        }
    }, [hasReadReportPermission, companyId, branchId, fromDate, toDate, token]);

    const handleSearch = (event) => setSearchQuery(event.target.value);

    const filteredDataItem = useMemo(() => {
        if (!searchQuery) return dataItem;
        const query = searchQuery.toLowerCase();
        return dataItem.filter((item) =>
            Object.values(item).some((value) =>
                String(value).toLowerCase().includes(query)
            )
        );
    }, [dataItem, searchQuery]);

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportExcel = () => {
        if (!filteredDataItem || filteredDataItem.length === 0) {
            console.warn("No data to export");
            return;
        }

        const categoryDataMap = {};
        filteredDataItem.forEach((rowData) => {
            let category = rowData.CategoryName ? rowData.CategoryName.trim() : "Unnamed Category";
            const key = category.toLowerCase();
            if (!categoryDataMap[key]) {
                categoryDataMap[key] = { name: category, rows: [] };
            }
            categoryDataMap[key].rows.push(rowData);
        });

        const workbook = new ExcelJS.Workbook();

        const headerRowValues = [
            'Code', 'Item', 'Opening', 'Purchase', 'Purchase Return', 'Sale', 'Sale Return', 'Stock In', 'Stock Out',
            'Replacement', 'Closing', 'MRP', 'Sale Value', 'GST%', 'HSN Code',
            'PG', 'IG', 'ISG', 'PF', 'Disc%', 'Category', 'Group', 'Season Code', 'Size Code', 'Exclusion'
        ];

        const rightAlignColumns = [
            'Opening', 'Purchase', 'Purchase Return', 'Sale', 'Sale Return', 'Stock In', 'Stock Out',
            'Replacement', 'Closing', 'Purchase Rate', 'MRP', 'Stock Value', 'Sale Value', 'GST%', 'Disc%'
        ];

        const safeSheetName = (name) => name.replace(/[:\\/?*\[\]]/g, "").substring(0, 31) || "Sheet";

        const addSheetWithData = (sheetName, caption, dataRows, totalRowArray) => {
            const worksheet = workbook.addWorksheet(safeSheetName(sheetName));
            worksheet.mergeCells(1, 1, 1, headerRowValues.length);
            const captionCell = worksheet.getCell(1, 1);
            captionCell.value = caption;
            captionCell.font = { size: 14, bold: true };
            captionCell.alignment = { vertical: 'middle', horizontal: 'left' };

            const headerRow = worksheet.addRow(headerRowValues);
            headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } };
            headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
            worksheet.views = [{ state: 'frozen', ySplit: 2 }];

            dataRows.forEach(row => {
                const addedRow = worksheet.addRow(row);
                addedRow.eachCell((cell, colNumber) => {
                    if (rightAlignColumns.includes(headerRowValues[colNumber - 1])) {
                        cell.alignment = { horizontal: 'right' };
                    }
                });
            });

            const totalRow = worksheet.addRow(totalRowArray);
            totalRow.eachCell((cell) => {
                cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
                cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4CAF50" } };
            });

            worksheet.columns = headerRowValues.map(() => ({ width: 15 }));
        };

        const allDataRows = filteredDataItem.map(rowData => [
            parseFloat(rowData.Code), rowData.Name,
            rowData.OpeningQty, rowData.PurchaseQty, rowData.PurchaseReturnQty,
            rowData.SaleQty, rowData.SaleReturnQty, rowData.StockInQty, rowData.StockOutQty,
            rowData.ReplaceQty, rowData.ClosingQty,
            parseFloat(rowData.MRP.toFixed(2)), parseFloat(rowData.SaleValue.toFixed(2)),
            rowData.GSTRate, rowData.HSNCode, rowData.PG, rowData.IG, rowData.ISG, rowData.PF,
            rowData.DiscountPercentage, rowData.CategoryName, rowData.ItemGroupName,
            rowData.SeasonCode, rowData.SizeCode, rowData.IsExcludingItem
        ]);

        const totalRowAll = ['', 'Total',
            filteredDataItem.reduce((a, b) => a + b.OpeningQty, 0),
            filteredDataItem.reduce((a, b) => a + b.PurchaseQty, 0),
            filteredDataItem.reduce((a, b) => a + b.PurchaseReturnQty, 0),
            filteredDataItem.reduce((a, b) => a + b.SaleQty, 0),
            filteredDataItem.reduce((a, b) => a + b.SaleReturnQty, 0),
            filteredDataItem.reduce((a, b) => a + b.StockInQty, 0),
            filteredDataItem.reduce((a, b) => a + b.StockOutQty, 0),
            filteredDataItem.reduce((a, b) => a + b.ReplaceQty, 0),
            filteredDataItem.reduce((a, b) => a + b.ClosingQty, 0),
            '', filteredDataItem.reduce((a, b) => a + parseFloat(b.SaleValue.toFixed(2)), 0),
            '', '', '', '', '', '', '', '', '', '', '', ''
        ];

        addSheetWithData(
            "All Data",
            `Consolidated Stock From: ${formatDate(fromDate)} To: ${formatDate(toDate)}`,
            allDataRows, totalRowAll
        );

        Object.values(categoryDataMap).forEach(({ name, rows }) => {
            const categoryRows = rows.map(rowData => [
                parseFloat(rowData.Code), rowData.Name,
                rowData.OpeningQty, rowData.PurchaseQty, rowData.PurchaseReturnQty,
                rowData.SaleQty, rowData.SaleReturnQty, rowData.StockInQty, rowData.StockOutQty,
                rowData.ReplaceQty, rowData.ClosingQty,
                parseFloat(rowData.MRP.toFixed(2)), parseFloat(rowData.SaleValue.toFixed(2)),
                rowData.GSTRate, rowData.HSNCode, rowData.PG, rowData.IG, rowData.ISG, rowData.PF,
                rowData.DiscountPercentage, rowData.CategoryName, rowData.ItemGroupName,
                rowData.SeasonCode, rowData.SizeCode, rowData.IsExcludingItem
            ]);

            const totalRowCategory = ['', 'Total',
                rows.reduce((a, b) => a + b.OpeningQty, 0),
                rows.reduce((a, b) => a + b.PurchaseQty, 0),
                rows.reduce((a, b) => a + b.PurchaseReturnQty, 0),
                rows.reduce((a, b) => a + b.SaleQty, 0),
                rows.reduce((a, b) => a + b.SaleReturnQty, 0),
                rows.reduce((a, b) => a + b.StockInQty, 0),
                rows.reduce((a, b) => a + b.StockOutQty, 0),
                rows.reduce((a, b) => a + b.ReplaceQty, 0),
                rows.reduce((a, b) => a + b.ClosingQty, 0),
                '', rows.reduce((a, b) => a + parseFloat(b.SaleValue.toFixed(2)), 0),
                '', '', '', '', '', '', '', '', '', '', '', ''
            ];

            addSheetWithData(
                name,
                `Consolidated Stock For Category: ${name} From: ${formatDate(fromDate)} To: ${formatDate(toDate)}`,
                categoryRows, totalRowCategory
            );
        });

        workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer]), `Consolidated_Stock_${formatDate(fromDate)}_${formatDate(toDate)}.xlsx`);
        });
    };

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Consolidated STOCK</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={handleFromDateChange} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={handleToDateChange} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={() => fetchTableData()} disabled={loading}>
                            <RefreshIcon /> {loading ? 'Loading...' : 'Refresh'}
                        </button>
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}>
                            <FeedIcon /> Excel
                        </button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearchInput(v => !v)}>
                            <SearchIcon /> Search
                        </button>
                    </div>
                    {showSearchInput && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={handleSearch} autoFocus style={{ marginTop: 6 }} />
                    )}
                </div>
            </div>

            {loading && <LoadingOverlay />}

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>Code</th>
                            <th>Item</th>
                            <th>Opening</th>
                            <th>Purchase</th>
                            <th>Purchase Return</th>
                            <th>Sale</th>
                            <th>Sale Return</th>
                            <th>Stock In</th>
                            <th>Stock Out</th>
                            <th>Replacement</th>
                            <th>Closing</th>
                            <th>MRP</th>
                            <th>Sale Value</th>
                            <th>GST%</th>
                            <th>HSN Code</th>
                            <th>PG</th>
                            <th>IG</th>
                            <th>ISG</th>
                            <th>PF</th>
                            <th>Disc%</th>
                            <th>Category</th>
                            <th>Group</th>
                            <th>Season Code</th>
                            <th>Size Code</th>
                            <th>Exclusion</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDataItem.map((data, index) => (
                            <tr key={index}>
                                <td>{data.Code}</td>
                                <td>{data.Name}</td>
                                <td style={{ textAlign: 'right' }}>{data.OpeningQty}</td>
                                <td style={{ textAlign: 'right' }}>{data.PurchaseQty}</td>
                                <td style={{ textAlign: 'right' }}>{data.PurchaseReturnQty}</td>
                                <td style={{ textAlign: 'right' }}>{data.SaleQty}</td>
                                <td style={{ textAlign: 'right' }}>{data.SaleReturnQty}</td>
                                <td style={{ textAlign: 'right' }}>{data.StockInQty}</td>
                                <td style={{ textAlign: 'right' }}>{data.StockOutQty}</td>
                                <td style={{ textAlign: 'right' }}>{data.ReplaceQty}</td>
                                <td style={{ textAlign: 'right' }}>{data.ClosingQty}</td>
                                <td style={{ textAlign: 'right' }}>{data.MRP.toFixed(2)}</td>
                                <td style={{ textAlign: 'right' }}>{data.SaleValue.toFixed(2)}</td>
                                <td style={{ textAlign: 'right' }}>{data.GSTRate}</td>
                                <td>{data.HSNCode}</td>
                                <td>{data.PG}</td>
                                <td>{data.IG}</td>
                                <td>{data.ISG}</td>
                                <td>{data.PF}</td>
                                <td>{data.DiscountPercentage}</td>
                                <td>{data.CategoryName}</td>
                                <td>{data.ItemGroupName}</td>
                                <td>{data.SeasonCode}</td>
                                <td>{data.SizeCode}</td>
                                <td>{data.IsExcludingItem}</td>
                            </tr>
                        ))}
                        <tr style={{ background: '#1c2a3a', color: '#fff', fontWeight: 700 }}>
                            <td colSpan={2}></td>
                            <td style={{ textAlign: 'right' }}>{totals.totalopbal}</td>
                            <td style={{ textAlign: 'right' }}>{totals.totalp}</td>
                            <td style={{ textAlign: 'right' }}>{totals.totalprtn}</td>
                            <td style={{ textAlign: 'right' }}>{totals.totals}</td>
                            <td style={{ textAlign: 'right' }}>{totals.totalsrtn}</td>
                            <td style={{ textAlign: 'right' }}>{totals.totalstin}</td>
                            <td style={{ textAlign: 'right' }}>{totals.totalstout}</td>
                            <td style={{ textAlign: 'right' }}>{totals.totalrpl}</td>
                            <td style={{ textAlign: 'right' }}>{totals.totalcls}</td>
                            <td></td>
                            <td style={{ textAlign: 'right' }}>{totals.totalsalval.toFixed(2)}</td>
                            <td colSpan={12}></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default Stock;
